import React, { useContext, useEffect, useState } from 'react';
import {
   Button, ButtonGroup, Card, CardBody, Col, Input,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import { toast } from "react-toastify";
import { QrReader, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, UserContext, dateHelpers, storage } from '../utils';

export default function ConeLookupMobile() {
   const { currentUser } = useContext(UserContext);
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const hasScanner = savedDevice.hasScanner;
   const isMobile = savedDevice.isMobile;
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const [data, setData] = useState("");
   const [id, setConeId] = useState(0);
   const [model, setModel] = useState({});
   const [manualEntry, setManual] = useState(null);
   const [openQr, setOpenQr] = useState(false);
   const [spin, setSpinner] = useState(false);

   useEffect(() => {
      inputFocus("scanInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (data && data.length > 0) {
         // A scan was completed
         const values = data.split(",");
         if (Array.isArray(values)) {
            // Test that the user scanned a cone label - the item number should NOT be numeric.
            if (values.length === 2 && isNaN(+values[0]))
               runQuery(values[1]);
            else
               toast.error("Read error: invalid cone label");
         }
         else {
            setData("");
            toast.error("Read error: invalid cone label");
         }
      }
      if (hasScanner) inputFocus("scanInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data])

   useEffect(() => {
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (id > 0)
         runQuery(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   function handleQrScan(val) {
      setOpenQr(false);  // Hide QR Scanner
      setData(val);
   }

   function handleQrScanFailure(val) {
      setOpenQr(false);  // Hide QR Scanner
      toast.warning("This device does not have a camera.");
   }

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function runQuery(coneId) {
      if (coneId && coneId > 0) {
         setSpinner(true);
         setData("");
         setConeId(0);
         setManual(null);
         setModel({});
         let lookup = true;
         api
            .fetch(`Production/GetProductionRecordByConeId/${coneId}/${lookup}`)
            .then(r => {
               if (r.data.success) {
                  if (r.data.message !== null && r.data.message !== undefined) {
                     setModel(r.data.message);
                  } else {
                     toast.error("No data returned!");
                  }
               } else {
                  toast.error(r.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setSpinner(false);
               inputFocus("scanInput");
            })
      }
      else {
         toast.error("Read error: not a valid cone label");
      }
   }

   return (
      <DesktopContainer screenName="Cone Lookup">
         <ServiceThreadSpinner on={spin}>
            <div className="filter-header" style={{ minWidth: '320px', maxWidth: '420px' }}>
               {!hasScanner && isMobile ? ( // NOT a handheld device, but still a mobile device
                  <Row className="justify-content-center">
                     <Col xs={11}>
                        <Button className="shredButton w-100 btn-md my-3" onClick={() => setOpenQr(!openQr)}>
                           {openQr ? "Hide Scanner" : "Scan QR Code"}
                        </Button>
                     </Col>
                  </Row>
               ) : null}

               {hasScanner ? ( // IS a Handheld device with built-in scanner
                  <div>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="justify-content-center">
                        <Col xs="11">
                           <Input
                              name="scanInput"
                              placeholder="Scan QR code"
                              className="h-100"
                              value={data}
                              onFocus={handleFocus}
                              onChange={(e) => handleQrScan(e.target.value)}
                           />
                        </Col>
                     </Row>
                  </div>
               ) : null}

               <Row className="lineheight8">&nbsp;</Row>
               <Row className="justify-content-center">
                  <Col xs={11}>
                     <Button className="shredButton w-100 btn-md my-3" onClick={() => setManual(1)}>
                        Manual Entry
                     </Button>
                  </Col>
               </Row>

               {openQr && <QrReader callback={handleQrScan} callbackError={handleQrScanFailure} />}
               <br />

               {model && model.item?.length > 0 ? ( // We have a production record
                  <Row className="justify-content-center">
                     <Col xs={11}>
                        <Card className="dashboard-card">
                           <CardBody>
                              <div><span className="h2 me-2">Cone Information</span></div>
                              <hr />
                              <Table className="transparent-background my-0">
                                 <tbody>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Cone #: {model.coneId}
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell lineheight8">
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Item:
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          &nbsp;&nbsp;{model.itemWithDesc}
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Machine:
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          &nbsp;&nbsp;{model.machineDisplay}
                                       </td>
                                    </tr>
                                    {model && model.pkgWeight > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Package Weight:
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.pkgWeight > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell h6">
                                          &nbsp;&nbsp;{model.pkgWeight > 0 ? model.pkgWeight : ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Status:
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          &nbsp;&nbsp;{model.defect || "In Process"}
                                       </td>
                                    </tr>
                                    {model && model.dateCompleted ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Completed On:
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.dateCompleted ? (
                                    <tr>
                                       <td className="transparent-background data-cell h6">
                                          &nbsp;&nbsp;{dateHelpers.dateTimeFormat(model.dateCompleted, dateHelpers.YMDHM) || ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.userName?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          Completed By:
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.userName?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell h6">
                                          &nbsp;&nbsp;{model.userName}
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.shiftDesc?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6">
                                          During:
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.shiftDesc?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell h6">
                                          &nbsp;&nbsp;{model.shiftDesc}
                                       </td>
                                    </tr>
                                    ) : null}
                                 </tbody>
                              </Table>
                           </CardBody>
                        </Card>
                     </Col>
                  </Row>
               ) : null}
            </div>

            <Modal isOpen={manualEntry !== null} contentClassName={modalClassName} style={{ minWidth: '320px', maxWidth: '320px' }}>
               <ModalHeader children="Enter Cone #" />
               <ModalBody
                  children={
                     <Input
                        type="number"
                        name="manualInput"
                        placeholder="Enter Cone #"
                        className="no-spinner h-100"
                        onChange={(e) => setConeId(e.target.value)}
                        onKeyPress={handleKeypress}
                     />
                  }
               />
               <ModalFooter>
                  <ButtonGroup>
                     <Button children="Retrieve" className="button-success" onClick={() => { setManual(null); }} />
                     <Button
                        children="Cancel"
                        className="button-danger"
                        onClick={() => {
                           setConeId(0);
                           setManual(null);
                           inputFocus("scanInput");
                        }}
                     />
                  </ButtonGroup>
               </ModalFooter>
            </Modal>
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}
